import * as R from 'ramda';
import * as P from 'plow-js';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature dispatch-details
import * as C from './constants';
/////////////////////////////////////////////

// events
export const getStatusMessageListWithLoadEvent = (
  list,
  events,
  loadType,
) => {
  const isTel = G.isLoadTypeTel(loadType);

  return R.map((item) => {
    const event = R.path([R.prop(GC.FIELD_STATUS_MESSAGE_LOAD_EVENT_GUID, item)], events);

    if (G.isNilOrEmpty(event)) return item;

    const loadEvent = G.getStopData(event, isTel);

    return R.assoc(GC.FIELD_STATUS_MESSAGE_LOAD_EVENT, loadEvent, item);
  }, list);
};

export const mapLoadStopsWithNewStatus = (load, stopGuid, status) => R.map(
  R.when(
    R.propEq(stopGuid, GC.FIELD_GUID),
    R.set(R.lensProp(GC.FIELD_LOAD_STATUS), status),
  ),
)(R.pathOr([], [GC.FIELD_LOAD_STOPS], load),
);

// reducer
export const getStateWithUnselectedRates = (state) => {
  const rates = R.map(
    R.assoc('selected', false),
    R.path(['lists', 'driverCarrierRateList'], state),
  );

  return P.$set('lists.driverCarrierRateList', rates, state);
};

export const setActiveListDataToStore = R.curry((
  state,
  list,
  listName,
  tableNumber,
) => {
  if (G.isNilOrEmpty(tableNumber)) return P.$set(`lists.${listName}`, list, state);
  if (R.equals(tableNumber, 'all')) {
    return P.$all(
      P.$set(`lists.${listName}`, list),
      P.$set('activeLists', R.map(() => listName, state.activeLists)),
      state,
    );
  }

  return P.$all(
    P.$set(`lists.${listName}`, list),
    P.$set(`activeLists.${tableNumber}`, listName),
    state,
  );
});

export const getListsWithFleetCarrierRateLists = (lists) => {
  const grouped = R.groupBy(R.prop(GC.FIELD_TYPE), R.propOr([], 'driverCarrierRateList', lists));

  return {
    ...lists,
    [C.ACTIVE_LIST_DRIVER_RATES]: R.propOr([], GC.RATE_TYPE_FLEET_RATE, grouped),
    [C.ACTIVE_LIST_CARRIER_RATES]: R.propOr([], GC.RATE_TYPE_CARRIER_RATE, grouped),
  };
};

export const setUpdatedListItemToStore = R.curry((data, state, listName) => {
  const { guid } = data;
  const list = R.path(['lists', listName], state);
  const index = R.findIndex(R.pathEq(guid, [GC.FIELD_GUID]), list);

  return P.$set(`lists.${listName}.${index}`, data, state);
});

export const removeListItemFromStore = R.curry((state, guid, listName) => {
  const list = R.path(['lists', listName], state);
  const index = R.findIndex(R.pathEq(guid, [GC.FIELD_GUID]), list);

  return P.$drop(`lists.${listName}.${index}`, state);
});

// customer rates
const getCloTotalWeightByCloGuid = (tel, cloGuid) => {
  const items = R.filter(
    R.pathEq(cloGuid, [GC.FIELD_LOAD_GUID]),
    R.pathOr([], [GC.FIELD_LOAD_ITEMS], tel),
  );
  if (G.isNotNilAndNotEmpty(items)) {
    const total = G.calcItemsTotalWeightWithoutQty(items);

    return G.renameKeys({
      [GC.FIELD_ITEM_WEIGHT]: GC.FIELD_TOTAL_TRIP_WEIGHT,
      [GC.FIELD_ITEM_WEIGHT_TYPE]: GC.FIELD_TOTAL_TRIP_WEIGHT_UOM,
    }, total);
  }

  return {};
};

export const makeCloTotalData = (clo, tel) => {
  const { guid } = clo;

  const cloTotalWeight = getCloTotalWeightByCloGuid(tel, guid);

  const cloEvents = R.filter(R.pathEq(guid, [GC.FIELD_CLO_GUID]), R.pathOr([], [GC.FIELD_LOAD_STOPS], tel));

  const mappedCloEvents = R.compose(
    R.filter((item) => G.isNotNilAndNotEmpty(item)),
    R.map(R.prop('cloDistanceToNextStop')),
  )(cloEvents);

  const cloTotalDistance = G.calculateTotalDistance(mappedCloEvents);

  const items = R.filter(
    R.pathEq(guid, [GC.FIELD_LOAD_GUID]),
    R.pathOr([], [GC.FIELD_LOAD_ITEMS], tel),
  );

  const cloTotalPickupQuantity = G.makeTotalPickupQuantityObjectFromItems(items);

  return {
    cloEvents,
    cloTotalWeight,
    cloTotalDistance,
    cloTotalPickupQuantity,
  };
};

export const getTotalCustomerRatesFromClos = (clos) => {
  if (G.isNotNilAndNotEmpty(clos)) {
    return R.reduce((acc, item) => {
      const total = R.pathOr(0, [GC.FIELD_TOTAL], item);

      return R.add(acc, total);
    }, 0, clos);
  }

  return 0;
};

// configs
export const getDefaultUomFieldsFromConfigs = (configsByNames) => {
  const uomSystem = G.getConfigValueFromStore(GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM, configsByNames);

  return R.pathOr({}, [uomSystem], GC.defaultSystemUoms);
};

export const getAvailableLegRateClos = (closInfo, legRates) => R.reject(
  (clo) => R.any(
    R.pathEq(G.getGuidFromObject(clo), [GC.FIELD_CLO_GUID]),
    legRates,
  ),
  closInfo,
);

export const withLegRateGuids = (values, cloGuid, telGuid) => R.mergeRight(
  values,
  { cloGuid, telGuid },
);

export const getLegRateFormData = ({ load, clo, configsByNames, defaultUomFields }) => {
  const cloGuid = G.getGuidFromObject(clo);
  const telGuid = G.getGuidFromObject(load);
  const equipment = R.path([GC.FIELD_LOAD_EQUIPMENT], clo);
  const services = R.pathOr([], [GC.FIELD_LOAD_SERVICES], clo);
  const cloTotalData = makeCloTotalData(clo, load);
  const stopCount = R.pathOr(0, ['cloEvents', 'length'], cloTotalData);
  const equipments = G.ifElse(G.isNilOrEmpty(equipment), [], R.of(Array, equipment));

  const uomFields = G.ifElse(
    G.isNotNilAndNotEmpty(defaultUomFields),
    defaultUomFields,
    getDefaultUomFieldsFromConfigs(configsByNames),
  );

  return {
    cloGuid,
    telGuid,
    stopCount,
    defaultUomFields: uomFields,
    events: cloTotalData.cloEvents,
    totalWeight: cloTotalData.cloTotalWeight,
    totalDistance: cloTotalData.cloTotalDistance,
    totalPickupQuantity: cloTotalData.cloTotalPickupQuantity,
    items: G.getItemsFromEventsUniqByGuid(cloTotalData.cloEvents),
    services: G.mapArrayOfObjectsToPropArray(GC.FIELD_DROPDOWN_OPTION_GUID, services),
    equipments: G.mapArrayOfObjectsToPropArray(GC.FIELD_DROPDOWN_OPTION_GUID, equipments),
  };
};

export const getSyncRateFromCustomerInvoiceValue = (loadConfigs, useConfigGroups = false) => {
  const configPath = G.ifElse(useConfigGroups, ['configGroups', GC.CLO_CONFIG_GROUP], ['configsByNames']);

  return G.getConfigValueFromStore(
    GC.CLO_RATE_SYNC_RATE_FROM_CUSTOMER_INVOICE,
    R.pathOr({}, configPath, loadConfigs),
  );
};

export const getCubicCapacityWarningErrors = (items, configs) => {
  const config = G.getConfigValueFromStore(GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM, configs);

  const { totalDensity, totalCubicVolume } = G.getTotalCubicCapacityValuesFromEvents({ items: [items], config });

  const showWarning = G.checkCubicCapacityRules({ config, totalDensity, totalCubicVolume });
  const isImperial = R.equals(config, GC.IMPERIAL_SYSTEM);

  const totalVolumeLocale = G.getWindowLocale(...G.ifElse(
    isImperial,
    ['titles:total-cubic-feet', 'Total Cubic Feet'],
    ['titles:total-cubic-metres', 'Total Cubic Metres'],
  ));

  return showWarning ? [
    `${totalVolumeLocale}: ${totalCubicVolume}`,
    `${G.getWindowLocale('titles:average-density', 'Average Density')}: ${totalDensity}`,
    G.getWindowLocale(
      'titles:cubic-capacity-warning',
      'This shipment exceeds recommended cubic capacity guidelines.',
    ),
  ] : null;
};

// status messages
export const filterStatusCodeOptions = (props, withoutComplited = false) => {
  let statusMessages = props.statusMessagesConfigs;

  const proofType = R.path(['values', GC.UI_FIELD_STATUS_MESSAGE_PROOF_TYPE], props);

  if (R.isNil(proofType)) return statusMessages;

  if (R.equals(proofType, GC.DOCUMENT_PROOF_TYPE_POP)) {
    statusMessages = R.filter(
      (status) => R.equals(status.statusType, GC.STATUS_CODE_STATUS_TYPE_PICKUP_COMPLETED),
      statusMessages,
    );
  }

  if (R.equals(proofType, GC.DOCUMENT_PROOF_TYPE_POD)) {
    statusMessages = R.filter(
      (status) => R.equals(status.statusType, GC.STATUS_CODE_STATUS_TYPE_DROP_COMPLETED),
      statusMessages,
    );
  }

  if (R.equals(withoutComplited, true)) {
    statusMessages = R.filter(
      (status) => (
        R.and(
          G.notEquals(status.statusType, GC.STATUS_CODE_STATUS_TYPE_DROP_COMPLETED),
          G.notEquals(status.statusType, GC.STATUS_CODE_STATUS_TYPE_PICKUP_COMPLETED),
        )
      ),
      statusMessages,
    );
  }

  return statusMessages;
};

export const getOptions = (setting) => R.compose(
  R.values,
  R.mapObjIndexed((label, value) => ({ value, label })),
)(setting);

export const createFieldsForReport = (setting) =>
  R.compose(
    R.addIndex(R.map)((key, index) =>
      ({ name: key, sequence: index })),
    R.keys,
  )(setting);

export const isLengthBetween8And16 = R.both(
  R.compose(R.gte(R.__, 8), R.length),
  R.compose(R.lte(R.__, 16), R.length),
);

export const sortAndFilterTabs = (tabs, uiReportFields) => {
  if (G.isNilOrEmpty(uiReportFields)) return tabs;

  return R.pipe(
    R.filter((item) => R.includes(R.prop(GC.FIELD_VALUE, item), uiReportFields)),
    R.sortBy((item) => R.indexOf(R.prop(GC.FIELD_VALUE, item), uiReportFields)),
  )(tabs);
};

export const getAvailableReportTabs = (tabs, uiReportFields) =>
  R.filter((tabName) => R.any(R.propEq(tabName, 'value'), tabs), uiReportFields);
